import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { MessageCircle, Brain, Database, Code, CheckCircle } from 'lucide-react';

const ProjectAIChatbotPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-purple-600 to-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <MessageCircle className="w-5 h-5" />
              <span className="font-semibold">AI & Machine Learning Project</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Build an AI Chatbot with NLP
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              Create an intelligent conversational AI that understands and responds to user queries using Natural Language Processing
            </p>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl p-8 md:p-12 border border-blue-100">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Project Overview</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              In this hands-on project, you'll build a fully functional AI-powered chatbot from scratch. You'll learn to implement natural language understanding, intent recognition, and context-aware responses — skills that are in high demand across industries.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">4-6 Weeks</div>
                <div className="text-gray-600">Duration</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-purple-600 mb-2">Beginner</div>
                <div className="text-gray-600">Level</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-green-600 mb-2">Certificate</div>
                <div className="text-gray-600">Upon Completion</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Build */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            What You'll Build
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: Brain,
                title: "Intent Recognition System",
                description: "Train your chatbot to understand user intentions and classify different types of queries"
              },
              {
                icon: MessageCircle,
                title: "Conversational Interface",
                description: "Design a user-friendly chat interface with real-time responses and conversation history"
              },
              {
                icon: Database,
                title: "Knowledge Base Integration",
                description: "Connect your bot to a knowledge base to provide accurate, contextual answers"
              },
              {
                icon: Code,
                title: "API Integration",
                description: "Integrate with OpenAI GPT or other LLM APIs for advanced natural language capabilities"
              }
            ].map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Skills You'll Learn */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Skills You'll Master
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Natural Language Processing (NLP)",
              "Machine Learning Fundamentals",
              "Python Programming",
              "API Integration (OpenAI, Dialogflow)",
              "Intent Classification & Entity Recognition",
              "Conversation Flow Design",
              "Database Management (MongoDB)",
              "Cloud Deployment (AWS/Azure)",
              "Testing & Debugging AI Models",
              "User Experience Design for Chatbots"
            ].map((skill, index) => (
              <div key={index} className="flex items-center gap-3 bg-blue-50 rounded-xl p-4">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-gray-800 font-medium">{skill}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Deliverables */}
      <section className="py-16 bg-gradient-to-br from-blue-600 to-purple-600 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
            Your Final Portfolio Piece
          </h2>
          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-white/20">
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 text-gray-900 font-bold">1</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Fully Functional AI Chatbot</h3>
                  <p className="text-blue-100">A working chatbot deployed on the cloud, ready to demonstrate to employers</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 text-gray-900 font-bold">2</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">GitHub Repository</h3>
                  <p className="text-blue-100">Well-documented code with README, showing your development process</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 text-gray-900 font-bold">3</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Project Presentation</h3>
                  <p className="text-blue-100">A professional demo video and documentation explaining your design decisions</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Ready to Build Your AI Chatbot?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join CareerLift and start building portfolio projects that get you hired
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.location.href = '/contact'}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold px-10 py-4 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
            >
              Start Learning
            </button>
            <button
              onClick={() => window.location.href = '/programs'}
              className="bg-gray-100 hover:bg-gray-200 text-gray-900 font-bold px-10 py-4 rounded-xl transition-all"
            >
              View All Projects
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProjectAIChatbotPage;
